/**
 * Category mapping utilities for course templates
 * Maps course names to academic categories with color coding
 */

// Standard academic categories with associated colors
export const CATEGORIES = {
  'Mathematics': '#F4D03F',
  'Physics': '#5DADE2',
  'Chemistry': '#58D68D',
  'Biology': '#F8B739',
  'Computer Science': '#AF7AC5',
  'History': '#5B7AE2',
  'Psychology': '#EC7063',
  'Economics': '#45B39D',
  'Philosophy': '#F5B041',
  'Engineering': '#5DADE2',
  'Art & Design': '#F1948A',
  'Literature': '#BB8FCE',
  'Business': '#52BE80',
  'General': '#95A5A6'
};

// Get category color
export const getCategoryColor = (category) => {
  return CATEGORIES[category] || CATEGORIES['General'];
};

// Infer category from course name (fallback if database category not set)
export const inferCategoryFromName = (courseName) => {
  const lowerName = courseName.toLowerCase();

  // Mathematics
  if (lowerName.includes('math') || lowerName.includes('calculus') ||
      lowerName.includes('algebra') || lowerName.includes('geometry') ||
      lowerName.includes('statistics') || lowerName.includes('trigonometry')) {
    return 'Mathematics';
  }

  // Physics
  if (lowerName.includes('physics') || lowerName.includes('quantum') ||
      lowerName.includes('mechanics') || lowerName.includes('thermodynamics') ||
      lowerName.includes('relativity')) {
    return 'Physics';
  }

  // Chemistry
  if (lowerName.includes('chemistry') || lowerName.includes('organic') ||
      lowerName.includes('chemical') || lowerName.includes('biochemistry')) {
    return 'Chemistry';
  }

  // Biology
  if (lowerName.includes('biology') || lowerName.includes('anatomy') ||
      lowerName.includes('genetics') || lowerName.includes('ecology') ||
      lowerName.includes('botany') || lowerName.includes('zoology')) {
    return 'Biology';
  }

  // Computer Science
  if (lowerName.includes('computer') || lowerName.includes('programming') ||
      lowerName.includes('software') || lowerName.includes('algorithm') ||
      lowerName.includes('data structure') || lowerName.includes('machine learning') ||
      lowerName.includes('artificial intelligence') || lowerName.includes('ai')) {
    return 'Computer Science';
  }

  // History
  if (lowerName.includes('history') || lowerName.includes('historical')) {
    return 'History';
  }

  // Psychology
  if (lowerName.includes('psychology') || lowerName.includes('cognitive') ||
      lowerName.includes('behavioral')) {
    return 'Psychology';
  }

  // Economics
  if (lowerName.includes('economics') || lowerName.includes('economy') ||
      lowerName.includes('microeconomics') || lowerName.includes('macroeconomics')) {
    return 'Economics';
  }

  // Philosophy
  if (lowerName.includes('philosophy') || lowerName.includes('philosophical') ||
      lowerName.includes('ethics') || lowerName.includes('logic')) {
    return 'Philosophy';
  }

  // Engineering
  if (lowerName.includes('engineering') || lowerName.includes('mechanical') ||
      lowerName.includes('electrical') || lowerName.includes('civil')) {
    return 'Engineering';
  }

  // Art & Design
  if (lowerName.includes('art') || lowerName.includes('design') ||
      lowerName.includes('visual') || lowerName.includes('graphic')) {
    return 'Art & Design';
  }

  // Literature
  if (lowerName.includes('literature') || lowerName.includes('writing') ||
      lowerName.includes('poetry') || lowerName.includes('novel')) {
    return 'Literature';
  }

  // Business
  if (lowerName.includes('business') || lowerName.includes('management') ||
      lowerName.includes('marketing') || lowerName.includes('finance')) {
    return 'Business';
  }

  // Default
  return 'General';
};

// Get all available categories as array
export const getAllCategories = () => {
  return Object.keys(CATEGORIES).filter(cat => cat !== 'General');
};

// Get category display name
export const getCategoryDisplayName = (category) => {
  return category || 'General';
};

// Weekly rotation helper - calculates week number (resets every Friday)
export const getCurrentWeekNumber = () => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 5 = Friday
  const currentDate = new Date(now);

  // Adjust to get the most recent Friday
  const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
  const daysSinceFriday = dayOfWeek <= 5 ? dayOfWeek + 2 : dayOfWeek - 5;
  currentDate.setDate(currentDate.getDate() - daysSinceFriday);

  // Calculate week number since epoch
  const weeksSinceEpoch = Math.floor(currentDate.getTime() / (7 * 24 * 60 * 60 * 1000));
  return weeksSinceEpoch;
};

// Deterministic shuffle based on seed (week number)
// This ensures the same 8 courses are shown during the same week
export const shuffleWithSeed = (array, seed) => {
  const shuffled = [...array];
  let currentIndex = shuffled.length;
  let temporaryValue, randomIndex;

  // Simple seeded random number generator
  const seededRandom = (max, seed) => {
    const x = Math.sin(seed++) * 10000;
    return Math.floor((x - Math.floor(x)) * max);
  };

  while (currentIndex !== 0) {
    randomIndex = seededRandom(currentIndex, seed);
    currentIndex -= 1;

    temporaryValue = shuffled[currentIndex];
    shuffled[currentIndex] = shuffled[randomIndex];
    shuffled[randomIndex] = temporaryValue;

    seed++;
  }

  return shuffled;
};

// Get featured courses for current week (client-side rotation)
export const getFeaturedCoursesForWeek = (allCourses, count = 8) => {
  if (!allCourses || allCourses.length === 0) return [];

  // Get current week number
  const weekNumber = getCurrentWeekNumber();

  // Take top courses by popularity (usage_count)
  const topCourses = [...allCourses]
    .filter(course => course.is_completed)
    .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))
    .slice(0, Math.min(30, allCourses.length)); // Top 30 courses

  // Shuffle deterministically based on week number
  const shuffled = shuffleWithSeed(topCourses, weekNumber);

  // Return first 8
  return shuffled.slice(0, count);
};
